/**
 * TOPIK Question API
 * Provides API for updating TOPIK questions and regenerating audio
 */

import { api } from '@/Data/api';
import ss from '../Session';
import { Question, QuestionGroupType, TopikResponse } from './types';

type UpdateQuestionParams = {
    examid: string;
    questionid: number;
    type?: 'multiple-choice' | 'fill-in-blank' | 'essay' | 'short-answer';
    text?: string;
    image?: string | null;
    audio?: { type: 'audio'; audio_url: string; audio_script: { role: string; gender?: string; age_group?: string; text: string; speed?: string }[] } | null;
    question_type?: string;
    choices?: { id: 'A' | 'B' | 'C' | 'D'; text: string; image_url?: string }[];
    correct_answer?: string;
    explanation?: string;
    points?: number;
    difficulty_level?: 1 | 2 | 3 | 4 | 5;
    essay_detail?: {
        word_limit_min?: number;
        word_limit_max?: number;
        evaluation_criteria?: string[];
        sample_answer?: string;
    };
};

type UpdateQuestionGroupParams = {
    groupid: string;
    shared_resource?: { type: 'audio'; audio_url: string; audio_script: { role: string; text: string }[] } | { type: 'text'; text: string } | null;
    shared_question_text?: string;
};

type RegenerateAudioParams = {
    examid: string;
    questionid: number;
};

type RegenerateGroupAudioParams = {
    examid: string;
    groupid: string;
};

export const updateQuestion = async (params: UpdateQuestionParams): Promise<Question | null> => {
    try {
        const res = await api.put<TopikResponse<Question>>('/topik/question', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to update question:', e);
        return null;
    }
};

export const updateQuestionGroup = async (params: UpdateQuestionGroupParams): Promise<Partial<QuestionGroupType> | null> => {
    try {
        const res = await api.put<TopikResponse<Partial<QuestionGroupType>>>('/topik/question-group', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to update question group:', e);
        return null;
    }
};

export const regenerateQuestionAudio = async (params: RegenerateAudioParams): Promise<{ status: string } | null> => {
    try {
        const res = await api.post<TopikResponse<{ status: string }>>('/topik/question', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to regenerate question audio:', e);
        return null;
    }
};

export const regenerateQuestionGroupAudio = async (params: RegenerateGroupAudioParams): Promise<{ status: string } | null> => {
    try {
        const res = await api.post<TopikResponse<{ status: string }>>('/topik/question-group', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to regenerate question group audio:', e);
        return null;
    }
};
